import Drawer from '../../Drawer';
import {
  formatPurchasePlanningCurrency,
  formatPurchasePlanningCashRequired,
  formatPurchasePlanningNumber,
  formatPurchasePlanningPercent,
} from '../../../lib/purchasePlanningHelpers';

export function PurchaseRoundingInspectDrawer({
  open,
  onClose,
  selectedRow,
  groupRows,
  marginFromSidebar,
}) {
  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      title={selectedRow ? `${selectedRow.supplySku} · Purchase rounding` : 'Purchase rounding'}
      showActionButtons={false}
      width="520px"
      marginFromSidebar={marginFromSidebar}
      bodyClassName="!p-0"
    >
      {selectedRow ? (
        <div className="flex h-full flex-col overflow-hidden">
          <p className="border-b border-gray-200 px-5 py-3 text-sm text-gray-500">
            {selectedRow.productName} · {selectedRow.supplier}
          </p>

          <div className="flex-1 space-y-4 overflow-auto px-5 py-4">
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900">Formula ladder</h3>
              <div className="mt-3 space-y-2 text-sm text-gray-600">
                <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <span>OTB units</span>
                  <span className="font-semibold text-gray-900">
                    {formatPurchasePlanningNumber(selectedRow.otbUnits)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2">
                  <span>MOQ buy (ceil OTB ÷ MOQ × MOQ)</span>
                  <span className="font-semibold text-gray-900">
                    {formatPurchasePlanningNumber(selectedRow.moqBuyUnits)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2">
                  <span>Base buy after carton rounding</span>
                  <span className="font-semibold text-gray-900">
                    {formatPurchasePlanningNumber(selectedRow.baseBuyUnits)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2">
                  <span>Fill units allocated</span>
                  <span className="font-semibold text-gray-900">
                    {formatPurchasePlanningNumber(selectedRow.fillAllocatedUnits)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-slate-50 px-3 py-2">
                  <span>Adjusted rounded units</span>
                  <span className="font-semibold text-gray-900">
                    {formatPurchasePlanningNumber(selectedRow.adjustedRoundedUnits)}
                  </span>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900">Governance interpretation</h3>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                This row is downstream of approved OTB demand. Any uplift from{' '}
                <span className="font-medium text-gray-900">
                  {formatPurchasePlanningNumber(selectedRow.otbUnits)}
                </span>{' '}
                to{' '}
                <span className="font-medium text-gray-900">
                  {formatPurchasePlanningNumber(selectedRow.adjustedRoundedUnits)}
                </span>{' '}
                reflects procurement validity, carton enforcement, or pooled container fill — not new demand.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Container group</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {selectedRow.containerGroup || 'Ungrouped'}
                  </p>
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Pre-fill utilization</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {formatPurchasePlanningPercent(selectedRow.preFillUtilization)}
                  </p>
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Cash required</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {formatPurchasePlanningCashRequired(selectedRow.cashRequired)}
                  </p>
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Exception status</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{selectedRow.exceptionSummary}</p>
                </div>
              </div>
            </section>

            {groupRows.length > 0 ? (
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900">Container pool membership</h3>
                <p className="mt-1 text-xs text-gray-500">
                  SKUs sharing this supplier pool before downstream planning reads adjusted rounded units.
                </p>
                <div className="mt-3 space-y-2">
                  {groupRows.map(row => (
                    <div
                      key={row.id}
                      className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0 pr-3">
                        <p className="truncate font-medium text-gray-900">{row.supplySku}</p>
                        <p className="truncate text-xs text-gray-500">{row.productName}</p>
                      </div>
                      <div className="text-right text-gray-600">
                        <p className="font-medium text-gray-900">
                          {formatPurchasePlanningNumber(row.adjustedRoundedUnits)}
                        </p>
                        <p className="text-xs">
                          {formatPurchasePlanningNumber(row.fillAllocatedUnits)} fill
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
